import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Mail,
  Webhook,
  Globe,
  Database,
  Plus,
  Trash2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Eye,
  EyeOff,
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

const ConnectionsPage = () => {
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [testing, setTesting] = useState(false);
  
  // Form state
  const [connectionName, setConnectionName] = useState("");
  const [connectionType, setConnectionType] = useState("email");
  const [config, setConfig] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/connections`, {
        withCredentials: true,
      });
      setConnections(response.data.connections || []);
    } catch (error) {
      console.error("Failed to fetch connections:", error);
      toast.error("Failed to load connections");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConnection = async () => {
    if (!connectionName.trim()) {
      toast.error("Please enter a connection name");
      return;
    }

    try {
      await axios.post(
        `${API}/connections`,
        {
          connection_name: connectionName,
          connection_type: connectionType,
          config: config,
          test_on_create: true,
        },
        { withCredentials: true }
      );

      toast.success("Connection created successfully");
      setShowCreateDialog(false);
      resetForm();
      fetchConnections();
    } catch (error) {
      console.error("Failed to create connection:", error);
      toast.error(
        error.response?.data?.detail || "Failed to create connection"
      );
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const response = await axios.post(
        `${API}/connections/test`,
        {
          connection_type: connectionType,
          config: config,
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("Connection test successful!");
      } else {
        toast.error(`Test failed: ${response.data.message}`);
      }
    } catch (error) {
      console.error("Connection test failed:", error);
      toast.error("Connection test failed");
    } finally {
      setTesting(false);
    }
  };

  const handleDeleteConnection = async (connectionId) => {
    if (!confirm("Are you sure you want to delete this connection?")) {
      return;
    }

    try {
      await axios.delete(`${API}/connections/${connectionId}`, {
        withCredentials: true,
      });
      toast.success("Connection deleted");
      fetchConnections();
    } catch (error) {
      console.error("Failed to delete connection:", error);
      toast.error("Failed to delete connection");
    }
  };

  const resetForm = () => {
    setConnectionName("");
    setConnectionType("email");
    setConfig({});
    setShowPassword(false);
  };

  const renderConfigForm = () => {
    switch (connectionType) {
      case "email":
        return (
          <div className="space-y-4">
            <div>
              <Label>Email Type</Label>
              <Select
                value={config.connection_type || "gmail_oauth"}
                onValueChange={(value) =>
                  setConfig({ ...config, connection_type: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gmail_oauth">Gmail (OAuth)</SelectItem>
                  <SelectItem value="imap_smtp">IMAP/SMTP</SelectItem>
                  <SelectItem value="sendgrid">SendGrid</SelectItem>
                  <SelectItem value="resend">Resend</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {config.connection_type === "gmail_oauth" && (
              <div>
                <Label>OAuth Token</Label>
                <Input
                  type={showPassword ? "text" : "password"}
                  value={config.oauth_token || ""}
                  onChange={(e) =>
                    setConfig({ ...config, oauth_token: e.target.value })
                  }
                  placeholder="Enter OAuth token"
                />
              </div>
            )}

            {config.connection_type === "imap_smtp" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>IMAP Host</Label>
                    <Input
                      value={config.imap_host || ""}
                      onChange={(e) =>
                        setConfig({ ...config, imap_host: e.target.value })
                      }
                      placeholder="imap.gmail.com"
                    />
                  </div>
                  <div>
                    <Label>IMAP Port</Label>
                    <Input
                      type="number"
                      value={config.imap_port || 993}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          imap_port: parseInt(e.target.value),
                        })
                      }
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>SMTP Host</Label>
                    <Input
                      value={config.smtp_host || ""}
                      onChange={(e) =>
                        setConfig({ ...config, smtp_host: e.target.value })
                      }
                      placeholder="smtp.gmail.com"
                    />
                  </div>
                  <div>
                    <Label>SMTP Port</Label>
                    <Input
                      type="number"
                      value={config.smtp_port || 587}
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          smtp_port: parseInt(e.target.value),
                        })
                      }
                    />
                  </div>
                </div>
                <div>
                  <Label>Username</Label>
                  <Input
                    value={config.username || ""}
                    onChange={(e) =>
                      setConfig({ ...config, username: e.target.value })
                    }
                    placeholder="your-email@example.com"
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={config.password || ""}
                      onChange={(e) =>
                        setConfig({ ...config, password: e.target.value })
                      }
                      placeholder="••••••••"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </>
            )}

            {(config.connection_type === "sendgrid" ||
              config.connection_type === "resend") && (
              <>
                <div>
                  <Label>API Key</Label>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={config.api_key || ""}
                    onChange={(e) =>
                      setConfig({ ...config, api_key: e.target.value })
                    }
                    placeholder="Enter API key"
                  />
                </div>
                <div>
                  <Label>From Email</Label>
                  <Input
                    type="email"
                    value={config.from_email || ""}
                    onChange={(e) =>
                      setConfig({ ...config, from_email: e.target.value })
                    }
                    placeholder="noreply@yourdomain.com"
                  />
                </div>
              </>
            )}
          </div>
        );

      case "webhook":
        return (
          <div className="space-y-4">
            <div>
              <Label>Webhook URL</Label>
              <Input
                value={config.webhook_url || ""}
                onChange={(e) =>
                  setConfig({ ...config, webhook_url: e.target.value })
                }
                placeholder="https://your-domain.com/webhook"
              />
            </div>
            <div>
              <Label>Secret (Optional)</Label>
              <Input
                type={showPassword ? "text" : "password"}
                value={config.secret || ""}
                onChange={(e) =>
                  setConfig({ ...config, secret: e.target.value })
                }
                placeholder="Webhook signing secret"
              />
            </div>
          </div>
        );

      case "http_api":
        return (
          <div className="space-y-4">
            <div>
              <Label>Base URL</Label>
              <Input
                value={config.base_url || ""}
                onChange={(e) =>
                  setConfig({ ...config, base_url: e.target.value })
                }
                placeholder="https://api.example.com"
              />
            </div>
            <div>
              <Label>Authentication Type</Label>
              <Select
                value={config.auth_type || "api_key"}
                onValueChange={(value) =>
                  setConfig({ ...config, auth_type: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="api_key">API Key</SelectItem>
                  <SelectItem value="bearer">Bearer Token</SelectItem>
                  <SelectItem value="basic">Basic Auth</SelectItem>
                  <SelectItem value="oauth2">OAuth 2.0</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {config.auth_type === "api_key" && (
              <>
                <div>
                  <Label>API Key</Label>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={config.api_key || ""}
                    onChange={(e) =>
                      setConfig({ ...config, api_key: e.target.value })
                    }
                    placeholder="Enter API key"
                  />
                </div>
                <div>
                  <Label>API Key Header</Label>
                  <Input
                    value={config.api_key_header || "X-API-Key"}
                    onChange={(e) =>
                      setConfig({ ...config, api_key_header: e.target.value })
                    }
                  />
                </div>
              </>
            )}

            {config.auth_type === "bearer" && (
              <div>
                <Label>Bearer Token</Label>
                <Input
                  type={showPassword ? "text" : "password"}
                  value={config.bearer_token || ""}
                  onChange={(e) =>
                    setConfig({ ...config, bearer_token: e.target.value })
                  }
                  placeholder="Enter bearer token"
                />
              </div>
            )}

            {config.auth_type === "basic" && (
              <>
                <div>
                  <Label>Username</Label>
                  <Input
                    value={config.username || ""}
                    onChange={(e) =>
                      setConfig({ ...config, username: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={config.password || ""}
                    onChange={(e) =>
                      setConfig({ ...config, password: e.target.value })
                    }
                  />
                </div>
              </>
            )}
          </div>
        );

      case "database":
        return (
          <div className="space-y-4">
            <div>
              <Label>Database Type</Label>
              <Select
                value={config.db_type || "postgres"}
                onValueChange={(value) =>
                  setConfig({ ...config, db_type: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="postgres">PostgreSQL</SelectItem>
                  <SelectItem value="mysql">MySQL</SelectItem>
                  <SelectItem value="mongodb">MongoDB</SelectItem>
                  <SelectItem value="firestore">Firestore</SelectItem>
                  <SelectItem value="supabase">Supabase</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(config.db_type === "postgres" || config.db_type === "mysql") && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Host</Label>
                    <Input
                      value={config.host || ""}
                      onChange={(e) =>
                        setConfig({ ...config, host: e.target.value })
                      }
                      placeholder="localhost"
                    />
                  </div>
                  <div>
                    <Label>Port</Label>
                    <Input
                      type="number"
                      value={
                        config.port || (config.db_type === "postgres" ? 5432 : 3306)
                      }
                      onChange={(e) =>
                        setConfig({
                          ...config,
                          port: parseInt(e.target.value),
                        })
                      }
                    />
                  </div>
                </div>
                <div>
                  <Label>Database Name</Label>
                  <Input
                    value={config.database || ""}
                    onChange={(e) =>
                      setConfig({ ...config, database: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Username</Label>
                  <Input
                    value={config.username || ""}
                    onChange={(e) =>
                      setConfig({ ...config, username: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={config.password || ""}
                    onChange={(e) =>
                      setConfig({ ...config, password: e.target.value })
                    }
                  />
                </div>
              </>
            )}

            {config.db_type === "mongodb" && (
              <div>
                <Label>Connection String</Label>
                <Input
                  type={showPassword ? "text" : "password"}
                  value={config.connection_string || ""}
                  onChange={(e) =>
                    setConfig({ ...config, connection_string: e.target.value })
                  }
                  placeholder="mongodb://..."
                />
              </div>
            )}

            {config.db_type === "supabase" && (
              <>
                <div>
                  <Label>Supabase URL</Label>
                  <Input
                    value={config.supabase_url || ""}
                    onChange={(e) =>
                      setConfig({ ...config, supabase_url: e.target.value })
                    }
                    placeholder="https://xxx.supabase.co"
                  />
                </div>
                <div>
                  <Label>Supabase Key</Label>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={config.supabase_key || ""}
                    onChange={(e) =>
                      setConfig({ ...config, supabase_key: e.target.value })
                    }
                  />
                </div>
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const getConnectionIcon = (type) => {
    switch (type) {
      case "email":
        return <Mail className="w-5 h-5" />;
      case "webhook":
        return <Webhook className="w-5 h-5" />;
      case "http_api":
        return <Globe className="w-5 h-5" />;
      case "database":
        return <Database className="w-5 h-5" />;
      default:
        return <Globe className="w-5 h-5" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-slate-950 dark:via-blue-950 dark:to-indigo-950">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Connections
            </h1>
            <p className="text-muted-foreground mt-2">
              Manage your email, webhook, API, and database connections
            </p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Connection
          </Button>
        </div>

        {/* Connections Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : connections.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Database className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">No connections yet</p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Connection
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {connections.map((connection) => (
              <Card key={connection.connection_id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10 text-primary">
                        {getConnectionIcon(connection.connection_type)}
                      </div>
                      <div>
                        <CardTitle className="text-lg">
                          {connection.connection_name}
                        </CardTitle>
                        <CardDescription className="capitalize">
                          {connection.connection_type.replace("_", " ")}
                        </CardDescription>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteConnection(connection.connection_id)}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    {connection.is_valid ? (
                      <Badge variant="success" className="bg-green-500/10 text-green-600 border-green-500/30">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        Connected
                      </Badge>
                    ) : (
                      <Badge variant="destructive">
                        <XCircle className="w-3 h-3 mr-1" />
                        Invalid
                      </Badge>
                    )}
                    {connection.last_tested && (
                      <span className="text-xs text-muted-foreground">
                        Tested {new Date(connection.last_tested).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Connection Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Connection</DialogTitle>
              <DialogDescription>
                Add a new connection to use in your workflows
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label>Connection Name</Label>
                <Input
                  value={connectionName}
                  onChange={(e) => setConnectionName(e.target.value)}
                  placeholder="My Email Connection"
                />
              </div>

              <div>
                <Label>Connection Type</Label>
                <Select value={connectionType} onValueChange={setConnectionType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        Email
                      </div>
                    </SelectItem>
                    <SelectItem value="webhook">
                      <div className="flex items-center gap-2">
                        <Webhook className="w-4 h-4" />
                        Webhook
                      </div>
                    </SelectItem>
                    <SelectItem value="http_api">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4" />
                        HTTP API
                      </div>
                    </SelectItem>
                    <SelectItem value="database">
                      <div className="flex items-center gap-2">
                        <Database className="w-4 h-4" />
                        Database
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {renderConfigForm()}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateDialog(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={handleTestConnection}
                disabled={testing}
              >
                {testing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Test Connection
                  </>
                )}
              </Button>
              <Button onClick={handleCreateConnection}>
                <Plus className="w-4 h-4 mr-2" />
                Create Connection
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ConnectionsPage;
